/**
 * チャイム音マスタ管理画面 (要件 3・4)。
 *
 * 放送開始 / 終了時に鳴らすチャイム音 (ChimeMaster) を CRUD する GUI。
 * ControlPanel API の `/chimes` エンドポイント群を利用する。
 *
 * 機能:
 *   - 一覧表示 (表示名 / 音源キー / 更新日時 / 操作)
 *   - 新規登録フォーム (表示名 + MP3 ファイル選択)
 *   - 表示名のインライン編集 / 音源の差し替え (PUT)
 *   - 試聴 (`GET /chimes/{chime_id}/audio` → Audio 再生)
 *   - 削除 (ConfirmDialog で確認 → DELETE)。放送地点から参照中の場合は API が 409 を返し、
 *     その旨をエラーメッセージとして表示する (参照切れ防止)。
 *
 * 登録したチャイムは放送地点編集モーダルの「放送開始 / 終了チャイム」セレクタから
 * 選択でき、UpdateContents が `contents_schedule[]` の先頭 / 末尾に `is_chime: true` の
 * エントリとして差し込む (既存音源との ffmpeg 合成は行わない)。
 *
 * ⚠️ Issue #524: 本画面には **多言語の放送文面 (SSML) を表示しない**。
 *    以前は右カラムに定型文マスタの多言語文面を参照表示していたが、
 *    音源管理という本画面の目的と無関係な SSML が大量に並び、一覧が見づらく
 *    なっていた。多言語文面の確認 / 編集は「定型文マスタ」画面
 *    (`/phrase-templates`) に一元化する。
 */
import { Spinner } from './Spinner';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { base64ToAudioUrl } from '../api/broadcastPoints';
import { ChimesClient } from '../api/chimes';
import { ApiError } from '../api/common';
import type { Chime } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useHistoryWarning } from '../hooks/useHistoryWarning';
import { formatDateTime } from '../utils/dateTime';
import { describeApiErrorMessage } from '../utils/apiErrorMessage';
import { judgeRemoteChange } from '../utils/remoteChange';
import { chimeTarget } from '../pages/masterHistoryTarget';

import { ConfirmDialog } from './ConfirmDialog';
import { MasterHistoryLink } from './MasterHistoryLink';

export interface ChimeMasterScreenProps {
  config: AppConfig;
}

/** 表示名の最大長 (バックエンド `MAX_CHIME_LABEL_LENGTH` と一致させる) */
const MAX_LABEL_LENGTH = 100;

/** インライン編集中の行の状態 */
interface EditState {
  /** 編集対象の chime_id */
  chime_id: string;
  /** 編集開始時の表示名 (変更判定に使う) */
  originalLabel: string;
  /** 編集開始時の更新日時 (LastUpdate)。更新確定時に他者の更新 / 削除を判定する基準 (Issue #765) */
  originalUpdatedAt: string | null;
  /** 入力中の表示名 */
  label: string;
  /** 差し替える音源ファイル (未選択なら現在の音源を維持) */
  file: File | null;
  /** 保存処理中フラグ */
  saving: boolean;
}

/** API クライアントの例外を画面表示用文字列に整形する */
function formatApiError(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) {
    return '放送地点から参照されているため削除できません。放送地点の設定を「（なし）」に変更してから削除してください';
  }
  return describeApiErrorMessage(err);
}

export function ChimeMasterScreen(props: ChimeMasterScreenProps): React.JSX.Element {
  const { config } = props;
  const { getIdToken } = useAuth();

  const apiClient = useMemo(() => new ChimesClient({ getIdToken, config }), [getIdToken, config]);
  /**
   * 編集を破棄するときの確認と、更新確定時の他者による更新 / 削除の確認 (Issue #765)。
   * 「実行してよろしいですか」は削除のみ (削除は専用の確認ダイアログを使う)。
   */
  const { confirmDiscard, guardedUpdate, confirmDialog } = useConfirmDialog();
  /** 保存後の「変更履歴の登録に失敗」警告 (マスタ変更履歴 SBI 8)。× で閉じるまで消えない */
  const { historyWarning, reportHistory } = useHistoryWarning('chime-history-warning');

  const [items, setItems] = useState<Chime[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** 一覧取得の世代カウンタ (古いレスポンスで新しい結果を上書きしないための防御) */
  const requestIdRef = useRef(0);

  // --- 新規登録フォーム ---
  const [formLabel, setFormLabel] = useState('');
  const [formFile, setFormFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /**
   * ファイル入力への ref。
   * 登録成功後に `value = ''` で選択状態をクリアするために使う
   * (`File` は state 側でクリアできるが、input 要素の表示名が残ってしまう)。
   */
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- インライン編集 ---
  const [edit, setEdit] = useState<EditState | null>(null);

  // --- 削除確認 ---
  const [deleteTarget, setDeleteTarget] = useState<Chime | null>(null);
  const [deleting, setDeleting] = useState(false);

  // --- 試聴 ---
  const [previewingId, setPreviewingId] = useState<string | null>(null);

  /** チャイム一覧を取得して state に反映する。 */
  const reload = useCallback(async (): Promise<void> => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await apiClient.list();
      if (currentRequestId !== requestIdRef.current) return;
      setItems(res.items);
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      setErrorMessage(formatApiError(err));
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiClient]);

  useEffect(() => {
    void reload();
    return () => {
      // アンマウント時に世代を進め、進行中リクエストの結果を破棄する。
      // eslint-disable-next-line react-hooks/exhaustive-deps -- ref は DOM ではなくリクエスト世代カウンタ
      ++requestIdRef.current;
    };
  }, [reload]);

  const canSubmit = formLabel.trim() !== '' && formFile !== null && !submitting && edit === null;

  /** 新規登録 (POST /chimes → リロード)。 */
  const handleAdd = useCallback(async (): Promise<void> => {
    if (!canSubmit || formFile === null) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const created = await apiClient.create(formLabel.trim(), formFile);
      reportHistory(created);
      setFormLabel('');
      setFormFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await reload();
    } catch (err) {
      setErrorMessage(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }, [apiClient, canSubmit, formFile, formLabel, reload, reportHistory]);

  /** 指定行の編集を開始する。 */
  const startEdit = useCallback((chime: Chime): void => {
    setErrorMessage(null);
    setEdit({
      chime_id: chime.chime_id,
      originalLabel: chime.label ?? '',
      originalUpdatedAt: chime.updated_at,
      label: chime.label ?? '',
      file: null,
      saving: false,
    });
  }, []);

  /**
   * 編集をキャンセルする。表示名を変えた・音源を選んだ場合は、更新せずに閉じることになるので破棄の確認を挟む (Issue #765)。
   */
  const cancelEdit = useCallback((): void => {
    if (edit !== null && (edit.label.trim() !== edit.originalLabel.trim() || edit.file !== null)) {
      confirmDiscard(() => setEdit(null));
      return;
    }
    setEdit(null);
  }, [confirmDiscard, edit]);

  const canSaveEdit =
    edit !== null &&
    !edit.saving &&
    edit.label.trim() !== '' &&
    (edit.label.trim() !== edit.originalLabel.trim() || edit.file !== null);

  /** 編集内容 (表示名 / 音源) を PUT で保存する。他者の更新 / 削除の確認は済んでいる前提。 */
  const executeSaveEdit = useCallback(async (): Promise<void> => {
    if (edit === null || !canSaveEdit) return;
    const target = edit;
    setEdit({ ...target, saving: true });
    setErrorMessage(null);
    try {
      const updated = await apiClient.update(target.chime_id, {
        label: target.label.trim(),
        ...(target.file ? { file: target.file } : {}),
      });
      reportHistory(updated);
      setEdit(null);
      await reload();
    } catch (err) {
      setErrorMessage(formatApiError(err));
      setEdit((prev) => (prev ? { ...prev, saving: false } : prev));
    }
  }, [apiClient, canSaveEdit, edit, reload, reportHistory]);

  /**
   * 保存ボタン: 開いてから他者が更新 / 削除していないかを確認してから保存する (Issue #765)。
   * 保存ボタンは変更があるときだけ有効なので、ここでは常に「自分の変更あり」として扱う。
   */
  const handleSaveEdit = useCallback((): void => {
    if (edit === null || !canSaveEdit) return;
    const target = edit;
    void guardedUpdate({
      hasChanges: true,
      fetchRemoteState: async () => {
        const res = await apiClient.list();
        return judgeRemoteChange(
          target.originalUpdatedAt,
          res.items.find((c) => c.chime_id === target.chime_id),
        );
      },
      execute: () => void executeSaveEdit(),
      onDeleted: () => {
        setEdit(null);
        void reload();
      },
    });
  }, [apiClient, canSaveEdit, edit, executeSaveEdit, guardedUpdate, reload]);

  /** 削除確定 (DELETE /chimes/{chime_id} → リロード)。参照中の場合は 409。 */
  const handleDeleteConfirmed = useCallback(async (): Promise<void> => {
    if (!deleteTarget || deleting) return;
    const target = deleteTarget;
    setDeleting(true);
    setErrorMessage(null);
    try {
      const removed = await apiClient.remove(target.chime_id);
      reportHistory(removed);
      setDeleteTarget(null);
      await reload();
    } catch (err) {
      setDeleteTarget(null);
      if (err instanceof ApiError && err.status === 404) {
        // 先に他者が削除済み: 削除したい状態にはなっているのでメッセージは出さず、一覧だけ更新する (Issue #765)。
        await reload();
        return;
      }
      // 409 (放送地点から参照中) 等はダイアログを閉じてエラー内容を提示する。
      setErrorMessage(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  }, [apiClient, deleteTarget, deleting, reload, reportHistory]);

  /** 音源を取得して再生する (試聴)。 */
  const handlePreview = useCallback(
    async (chime: Chime): Promise<void> => {
      setErrorMessage(null);
      setPreviewingId(chime.chime_id);
      try {
        const { content_type, content_base64 } = await apiClient.getAudio(chime.chime_id);
        const audio = new Audio(base64ToAudioUrl(content_base64, content_type));
        await audio.play();
      } catch (err) {
        setErrorMessage(formatApiError(err));
      } finally {
        setPreviewingId(null);
      }
    },
    [apiClient],
  );

  return (
    <section className="section section--page list-page" data-testid="chime-master-section">
      <h2>
        チャイム音設定
        {loading ? (
          <>
            <Spinner />
            (読み込み中...)
          </>
        ) : (
          ` (${items.length} 件)`
        )}
      </h2>

      {historyWarning}

      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#555' }}>
        放送開始 / 終了時に鳴らすチャイム音 (MP3) を登録します。
        登録した音源は放送地点の編集画面で「放送開始チャイム」「放送終了チャイム」として
        地点単位に選択できます。
      </p>

      {errorMessage ? (
        <div role="alert" className="error-message" data-testid="chime-master-error">
          {errorMessage}
        </div>
      ) : null}

      <form
        className="alert-recipient-form"
        data-testid="chime-master-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) void handleAdd();
        }}
      >
        <label>
          表示名
          <input
            type="text"
            value={formLabel}
            onChange={(e) => setFormLabel(e.target.value)}
            placeholder="例: ポーン（開始）"
            maxLength={MAX_LABEL_LENGTH}
            required
            data-testid="chime-form-label"
          />
        </label>
        <label>
          音源ファイル (MP3)
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/mpeg,.mp3"
            onChange={(e) => setFormFile(e.target.files?.[0] ?? null)}
            required
            data-testid="chime-form-file"
          />
          <span className="field-hint">MP3 のみ / 最大 5 MiB</span>
        </label>
        <button
          type="submit"
          className="primary"
          disabled={!canSubmit}
          data-testid="chime-form-add-button"
        >
          {submitting ? (
            <>
              <Spinner />
              登録中...
            </>
          ) : (
            '登録'
          )}
        </button>
        <button
          type="button"
          onClick={() => void reload()}
          disabled={edit !== null}
          data-testid="chime-master-reload-button"
        >
          再読み込み
        </button>
      </form>

      {/*
        Issue #524: 多言語文面 (SSML) の参照パネルを撤去し 1 カラムへ戻した。
        本画面は音源管理に専念させ、文面の確認 / 編集は「定型文設定」画面に任せる。
      */}
      {items.length === 0 ? (
        <p className="data-table__empty" data-testid="chime-master-empty">
          {loading ? (
            <>
              <Spinner />
              読み込み中...
            </>
          ) : (
            '登録されているチャイム音がありません'
          )}
        </p>
      ) : (
        <div className="list-page__body" data-testid="chime-master-list-body">
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">表示名</th>
                  <th scope="col">音源キー</th>
                  <th scope="col">更新日時</th>
                  <th scope="col">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((chime) => {
                  const rowKey = encodeURIComponent(chime.chime_id);
                  const isEditing = edit !== null && edit.chime_id === chime.chime_id;
                  if (isEditing && edit) {
                    return (
                      <tr key={rowKey} data-testid={`chime-row-${rowKey}`}>
                        <td>
                          <input
                            type="text"
                            value={edit.label}
                            onChange={(e) =>
                              setEdit((prev) => (prev ? { ...prev, label: e.target.value } : prev))
                            }
                            aria-label={`${chime.label ?? chime.chime_id} の表示名(編集)`}
                            maxLength={MAX_LABEL_LENGTH}
                            disabled={edit.saving}
                            data-testid={`chime-edit-label-${rowKey}`}
                          />
                        </td>
                        <td>
                          <input
                            type="file"
                            accept="audio/mpeg,.mp3"
                            onChange={(e) =>
                              setEdit((prev) =>
                                prev ? { ...prev, file: e.target.files?.[0] ?? null } : prev,
                              )
                            }
                            aria-label={`${chime.label ?? chime.chime_id} の音源を差し替え`}
                            disabled={edit.saving}
                            data-testid={`chime-edit-file-${rowKey}`}
                          />
                          <span className="field-hint">未選択なら現在の音源を維持します</span>
                        </td>
                        <td>{formatDateTime(chime.updated_at)}</td>
                        <td>
                          <button
                            type="button"
                            className="primary"
                            onClick={handleSaveEdit}
                            disabled={!canSaveEdit}
                            data-testid={`chime-save-button-${rowKey}`}
                          >
                            {edit.saving ? (
                              <>
                                <Spinner />
                                保存中...
                              </>
                            ) : (
                              '保存'
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            disabled={edit.saving}
                            data-testid={`chime-cancel-button-${rowKey}`}
                          >
                            キャンセル
                          </button>
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={rowKey} data-testid={`chime-row-${rowKey}`}>
                      <td>{chime.label ?? '—'}</td>
                      <td>
                        <code style={{ fontSize: 12 }}>{chime.audio_key ?? '—'}</code>
                      </td>
                      <td>{formatDateTime(chime.updated_at)}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => void handlePreview(chime)}
                          disabled={edit !== null || previewingId === chime.chime_id}
                          aria-label={`${chime.label ?? chime.chime_id} を試聴`}
                          data-testid={`chime-preview-button-${rowKey}`}
                        >
                          {previewingId === chime.chime_id ? '再生中...' : '▶ 試聴'}
                        </button>
                        <button
                          type="button"
                          onClick={() => startEdit(chime)}
                          disabled={edit !== null}
                          aria-label={`${chime.label ?? chime.chime_id} を編集`}
                          data-testid={`chime-edit-button-${rowKey}`}
                        >
                          編集
                        </button>
                        <button
                          type="button"
                          className="danger"
                          onClick={() => setDeleteTarget(chime)}
                          disabled={edit !== null}
                          aria-label={`${chime.label ?? chime.chime_id} を削除`}
                          data-testid={`chime-delete-button-${rowKey}`}
                        >
                          削除
                        </button>
                        <MasterHistoryLink
                          target={chimeTarget(chime.chime_id)}
                          subject={chime.label ?? chime.chime_id}
                          testId={`chime-history-link-${rowKey}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="チャイム音の削除"
        message={
          deleteTarget
            ? `チャイム音 "${deleteTarget.label ?? deleteTarget.chime_id}" を削除します。よろしいですか？\n` +
              '放送地点から参照中の場合は削除できません (先に放送地点側の設定を「（なし）」に変更してください)。'
            : ''
        }
        confirmLabel="削除"
        destructive
        confirmDisabled={deleting}
        cancelDisabled={deleting}
        onConfirm={() => void handleDeleteConfirmed()}
        onCancel={() => setDeleteTarget(null)}
      />

      {confirmDialog}
    </section>
  );
}
